import * as yup from "yup";
import { Button } from "@components/buttons/button";
import { Input } from "@components/forms/input";
import { InputGroup } from "@components/forms/input-group";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { config } from "config";

// Images
import logo from "@assets/img/logo/logo-symbol.svg";
import logo_text from "@assets/img/logo/logo-text.svg";
import imgLogin from "@assets/img/card_1.png";
import { ZuAuthStore } from "@hooks/stores/zustand-auth-store";

interface IFormValues {
  email: string;
  password: string;
}

export function Auth() {
  const { customer } = ZuAuthStore();
  const navigate = useNavigate();
  if (customer) navigate(`/home`);

  const scheme = yup.object().shape({
    email: yup
      .string()
      .required("E-mail é obrigatório")
      .email("Digite um e-mail válido"),
    password: yup.string().required("Senha é obrigatória"),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupResolver(scheme) as any,
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  const { signIn } = useAuthContext();
  const currentYear = new Date().getFullYear();

  async function handleLogin(data: IFormValues) {
    await signIn(data);
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === "Enter") {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        activeElement.blur();
      }
      event.preventDefault();
      handleSubmit(handleLogin)();
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
        }}
      >
        {/* Header */}
        <div
          className="container-fluid"
          style={{
            paddingTop: "1rem",
            paddingBottom: "1.5rem",
            paddingLeft: "clamp(1rem, 5vw, 10rem)",
            paddingRight: "clamp(1rem, 5vw, 10rem)",
          }}
        >
          <div className="row">
            <div className="col-md-12">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                {/* Logo Side */}
                <div className="col-12 col-md-6 text-center text-md-start">
                  <a href="/" style={{ cursor: "pointer" }}>
                    <img
                      src={logo}
                      style={{ height: "clamp(24px, 10vw, 50px)" }}
                      alt="Logo"
                    />
                    <img
                      src={logo_text}
                      style={{
                        height: "clamp(15px, 10vw, 25px)",
                        marginLeft: "1rem",
                      }}
                      alt="Logo"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="container-fluid d-flex align-items-center justify-content-center py-4">
          <div className="row justify-content-center w-100">
            <div className="col-12 col-md-8 col-lg-6 col-xl-5">
              {/* Image Above of the Form */}
              <div className="text-center">
                <img
                  src={imgLogin}
                  className="img-fluid rounded-4"
                  style={{
                    maxHeight: "300px",
                    width: "100%",
                    maxWidth: "600px",
                    objectFit: "cover",
                  }}
                  alt="imgLogin"
                />
              </div>

              {/* Sign-In Form */}
              <div className="d-flex flex-column justify-content-center align-items-center">
                <div className="col-12 d-flex flex-column px-3 px-md-4">
                  <span className="text-center fs-3 fw-bold mt-3">
                    Conectar-se
                  </span>
                  <span className="text-center fs-6 fs-md-5 text-muted my-3 mb-md-2">
                    Acesse a sua Dashboard agora mesmo.
                  </span>

                  {/* Form */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit(handleLogin)();
                    }}
                  >
                    <div className="row mt-3">
                      <div className="col-md-12 d-flex flex-column">
                        {/* E-Mail */}
                        <div className="col-md-12 mb-3">
                          <Input
                            label=""
                            placeholder="E-mail"
                            showLabel={false}
                            type="text"
                            register={register("email")}
                            errorMessage={errors?.email?.message}
                          />
                        </div>

                        {/* Password */}
                        <div className="col-md-12">
                          <InputGroup
                            label=""
                            placeholder="Senha"
                            type="password"
                            register={register("password")}
                            errorMessage={errors?.password?.message}
                          />
                        </div>

                        {/* Forgot the password? */}
                        <a
                          className="mt-1"
                          onClick={() => navigate("/password-recover")}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="fs-6 text-black">
                            Esqueceu a senha?
                          </span>
                        </a>

                        {/* Submit Button */}
                        <div className="col-md-12 mt-4 col-12">
                          <Button
                            children="Entrar"
                            className="btn btn-success text-white w-100 py-3"
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="d-flex flex-column justify-content-center text-center"
          style={{ marginTop: "1.5rem", marginBottom: "2.5rem" }}
        >
          <span className="">
            © {new Date().getFullYear()}, {config.appName}, feito por{" "}
            <i className="fa fa-heart heart text-danger"></i>
            <a href="http://www.code2business.com/" target="_blank">
              Code2Business
            </a>{" "}
            Ltda.
          </span>
          <span className="">Todos os direitos reservados</span>
        </div>
      </div>
    </>
  );
}
